// =============================================================================
// Repeat rule editor of the event form: a preset dropdown (never / daily /
// weekly / monthly / yearly / custom) and, for custom rules, frequency,
// interval, weekdays and how the series ends.
// =============================================================================

import React from 'react';
import { Repeat } from 'lucide-react';
import { format, addDays, parseISO } from 'date-fns';
import { makePresetRecurrence, detectPreset, isoWeekday } from '../../utils/recurrence';
import { useTranslation } from '../../lib/i18n';

// Dictionary keys per option; the labels are looked up at render time.
const PRESET_LABELS = {
  none: 'calendar.repeatNever',
  daily: 'calendar.repeatDaily',
  weekly: 'calendar.repeatWeekly',
  monthly: 'calendar.repeatMonthly',
  yearly: 'calendar.repeatYearly',
  custom: 'calendar.repeatCustom'
};

const FREQ_LABELS = {
  daily: 'calendar.repeatDaily',
  weekly: 'calendar.repeatWeekly',
  monthly: 'calendar.repeatMonthly',
  yearly: 'calendar.repeatYearly'
};

const FREQ_UNIT_SINGULAR = {
  daily: 'calendar.unitDay',
  weekly: 'calendar.unitWeek',
  monthly: 'calendar.unitMonth',
  yearly: 'calendar.unitYear'
};
const FREQ_UNIT_PLURAL = {
  daily: 'calendar.unitDays',
  weekly: 'calendar.unitWeeks',
  monthly: 'calendar.unitMonths',
  yearly: 'calendar.unitYears'
};

// ISO weekdays Monday..Sunday; names come from the date-fns locale.
const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

const RecurrenceEditor = ({ date, recurrence, setRecurrence }) => {
  const { t, locale } = useTranslation();
  const weekdayLabel = (iso) => locale.localize.day(iso % 7, { width: 'wide' });
  const preset = detectPreset(recurrence, date);
  const isCustom = preset === 'custom';

  const handlePresetChange = (next) => {
    if (next === 'none') {
      setRecurrence(null);
    } else {
      // Preserve per-occurrence deletions — replacing the recurrence object
      // wholesale would drop `exceptions` and resurrect deleted occurrences.
      const exceptions = recurrence?.exceptions;
      const rec = makePresetRecurrence(next, date);
      setRecurrence(exceptions && exceptions.length ? { ...rec, exceptions } : rec);
    }
  };

  const updateRec = (patch) => setRecurrence({ ...(recurrence || {}), ...patch });

  const toggleWeekday = (iso) => {
    const wds = new Set(recurrence?.weekdays || []);
    if (wds.has(iso)) wds.delete(iso);
    else wds.add(iso);
    updateRec({ weekdays: Array.from(wds).sort((a, b) => a - b) });
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
        <Repeat size={14} className="text-gray-500" />
        {t('calendar.recurrence')}
      </label>
      <select
        className="input"
        value={preset}
        onChange={(e) => handlePresetChange(e.target.value)}
      >
        {Object.entries(PRESET_LABELS).map(([key, labelKey]) => (
          <option key={key} value={key}>
            {t(labelKey)}
          </option>
        ))}
      </select>

      {isCustom && recurrence && (
        <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
          {/* Häufigkeit */}
          <div className="flex items-center justify-between gap-3">
            <label className="text-sm text-gray-700">{t('calendar.frequency')}</label>
            <select
              className="bg-white border border-gray-300 rounded px-2 py-1 text-sm"
              value={recurrence.freq}
              onChange={(e) => {
                const next = { ...recurrence, freq: e.target.value };
                if (e.target.value !== 'weekly') delete next.weekdays;
                if (e.target.value === 'weekly' && !recurrence.weekdays) {
                  next.weekdays = [isoWeekday(parseISO(date))];
                }
                setRecurrence(next);
              }}
            >
              {Object.entries(FREQ_LABELS).map(([key, labelKey]) => (
                <option key={key} value={key}>
                  {t(labelKey)}
                </option>
              ))}
            </select>
          </div>

          {/* Interval */}
          <div className="flex items-center justify-between gap-3">
            <label className="text-sm text-gray-700">{t('calendar.every')}</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={99}
                value={recurrence.interval || 1}
                onChange={(e) =>
                  updateRec({ interval: Math.max(1, Number(e.target.value) || 1) })
                }
                className="w-16 bg-white border border-gray-300 rounded px-2 py-1 text-sm text-right"
              />
              <span className="text-sm text-gray-600 w-20">
                {t(
                  (recurrence.interval || 1) === 1
                    ? FREQ_UNIT_SINGULAR[recurrence.freq]
                    : FREQ_UNIT_PLURAL[recurrence.freq]
                )}
              </span>
            </div>
          </div>

          {/* Weekdays (only weekly) */}
          {recurrence.freq === 'weekly' && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-2">{t('calendar.fixedDay')}</p>
              <div className="space-y-1">
                {ISO_WEEKDAYS.map((iso) => {
                  const active = (recurrence.weekdays || []).includes(iso);
                  return (
                    <button
                      key={iso}
                      type="button"
                      onClick={() => toggleWeekday(iso)}
                      className="w-full flex items-center justify-between px-2 py-1.5 bg-white rounded border border-gray-200 text-sm hover:bg-gray-50"
                    >
                      <span className="text-gray-700">{weekdayLabel(iso)}</span>
                      <span
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          active ? 'bg-primary-600' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            active ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* End */}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-2">{t('calendar.repeatEnd')}</p>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="endType"
                  checked={(recurrence.endType || 'never') === 'never'}
                  onChange={() => updateRec({ endType: 'never' })}
                />
                <span>{t('calendar.repeatNever')}</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="endType"
                  checked={recurrence.endType === 'count'}
                  onChange={() => updateRec({ endType: 'count', count: recurrence.count || 10 })}
                />
                <span>{t('calendar.repeatCount')}</span>
                <input
                  type="number"
                  min={1}
                  max={999}
                  disabled={recurrence.endType !== 'count'}
                  value={recurrence.count || 10}
                  onChange={(e) =>
                    updateRec({ count: Math.max(1, Number(e.target.value) || 1) })
                  }
                  className="ml-auto w-20 bg-white border border-gray-300 rounded px-2 py-1 text-sm text-right disabled:opacity-50"
                />
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="endType"
                  checked={recurrence.endType === 'until'}
                  onChange={() =>
                    updateRec({
                      endType: 'until',
                      until: recurrence.until || format(addDays(parseISO(date), 30), 'yyyy-MM-dd')
                    })
                  }
                />
                <span>{t('calendar.repeatUntil')}</span>
                <input
                  type="date"
                  disabled={recurrence.endType !== 'until'}
                  value={recurrence.until || ''}
                  min={date}
                  onChange={(e) => updateRec({ until: e.target.value })}
                  className="ml-auto bg-white border border-gray-300 rounded px-2 py-1 text-sm disabled:opacity-50"
                />
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecurrenceEditor;
